import type React from "react"
import { Radio, Space, Card, Typography, Select, InputNumber } from "antd"
import type { RadioChangeEvent } from "antd"
import { PREDEFINED_PROMPTS, customPromptTemplate } from "common/Utility"
import type { BusinessArea } from "types/components/IAITagGenerationWizard"

const { Title, Text, Paragraph } = Typography


interface SelectPromptStepProps {
  selectedPromptId: string | null
  createCustomPrompt: boolean
  selectedBusinessArea: string
  requestedTagCount: number
  businessAreaList: Array<BusinessArea>
  setSelectedPromptId: (id: string | null) => void
  setCreateCustomPrompt: (value: boolean) => void
  setCustomPrompt: (prompt: string) => void
  setRequestedTagCount: (count: number) => void
  handleBusinessAreaChange: (value: string) => void
  handlePromptChange: (e: RadioChangeEvent) => void
}

export const SelectPromptStep: React.FC<SelectPromptStepProps> = ({
  selectedPromptId,
  createCustomPrompt,
  selectedBusinessArea,
  requestedTagCount,
  businessAreaList,
  setSelectedPromptId,
  setCreateCustomPrompt,
  setCustomPrompt,
  setRequestedTagCount,
  handleBusinessAreaChange,
  handlePromptChange,
}) => {
  return (
    <div className="space-y-6 mt-2">
      <Title level={4}>Select a Prompt Template</Title>
      <Paragraph>
        Choose a predefined prompt template for your industry or create your own. You can refine it in the next step.
      </Paragraph>

      <div className="mb-1">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <Text strong>Business Area</Text>
            <Select
              showSearch={{
                filterOption:(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }}
              className="w-full mt-2"
              value={selectedBusinessArea}
              onChange={handleBusinessAreaChange}
              placeholder="Select business area"
              options={businessAreaList.map((area) => ({
                value: area.businessAreaId,
                label: area.name,
              }))}
            />
          </div>
          <div>
            <Text strong>Number of Tags to Generate</Text>
            <div className="flex items-center mt-2">
              <InputNumber
                min={5}
                max={50}
                value={requestedTagCount}
                onChange={(e) => setRequestedTagCount(Number(e))}
                controls={true}
                keyboard={false}
                className="mr-2"
              />
            </div>
            <Text type="secondary" className="block mt-1 text-xs font-medium">
              Select between 5-50 tags to generate
            </Text>
          </div>
        </div>
        <Radio.Group onChange={handlePromptChange} value={selectedPromptId || (createCustomPrompt ? "custom" : null)}>
        <div className="grid grid-cols-2 gap-2">
            {PREDEFINED_PROMPTS.map((prompt) => (
              <Card
                hoverable
                key={prompt.id}
                className={` cursor-pointer hover:shadow-md transition-shadow p-2 ${
                  selectedPromptId === prompt.id ? "border-blue-500 border-2" : ""
                }`}
                onClick={() => {
                  setSelectedPromptId(prompt.id)
                  setCreateCustomPrompt(false)
                  setCustomPrompt(prompt.prompt)
                }}
              >
                <Radio value={prompt.id} className="font-bold">
                  {prompt.title}
                </Radio>
                <Paragraph className="mt-2 text-gray-600 text-sm ml-6 line-clamp-2 leading-tight">{prompt.prompt}</Paragraph>
              </Card>
            ))}
            <Card
              hoverable
              className={` cursor-pointer hover:shadow-md transition-shadow p-3 ${
                createCustomPrompt ? "border-blue-500 border-2" : ""
              }`}
              onClick={() => {
                setSelectedPromptId(null)
                setCreateCustomPrompt(true)
                setCustomPrompt(customPromptTemplate)
              }}
            >
              <Radio value="custom" className="font-bold">
                Custom 
              </Radio>
              <Paragraph className="mt-2 text-gray-600 text-sm ml-6">
                Create a completely custom prompt for your specific needs. 
              </Paragraph>
            </Card>
          </div>
        </Radio.Group>
      </div>
    </div>
  )
}
